import React from 'react';
import DocLayout from '../DocLayout';
import { Sparkles, Database, TrendingUp } from 'lucide-react';

const Enrichment = () => {
  return (
    <DocLayout title="Lead Enrichment" category="AI Features">
      <div className="space-y-6">
        <section>
          <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-indigo-600" />
            AI-Powered Data Enrichment
          </h2>
          <p className="text-slate-700 mb-4">
            Transform incomplete lead data into rich, actionable profiles with I-Match's AI enrichment engine.
          </p>
        </section>

        <section>
          <h3 className="text-xl font-semibold text-slate-900 mb-3">What is Lead Enrichment?</h3>
          <p className="text-slate-700 mb-3">
            Lead enrichment automatically fills in missing company and contact information using AI and web data sources. With just a name and email, I-Match can discover:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <ul className="list-disc pl-6 space-y-1 text-slate-700 text-sm">
              <li>Company overview & description</li>
              <li>Industry and business model</li>
              <li>Company size & employee count</li>
              <li>Revenue estimates</li>
              <li>Products and services</li>
              <li>Key leadership team</li>
            </ul>
            <ul className="list-disc pl-6 space-y-1 text-slate-700 text-sm">
              <li>Market presence & competitors</li>
              <li>Technology stack</li>
              <li>Recent news and activity</li>
              <li>Social media profiles</li>
              <li>Headquarters location</li>
              <li>Lead qualification insights</li>
            </ul>
          </div>
        </section>

        <section>
          <h3 className="text-xl font-semibold text-slate-900 mb-3">How to Enrich a Lead</h3>
          <ol className="list-decimal pl-6 space-y-3 text-slate-700">
            <li>
              <strong>Single Lead Enrichment</strong>
              <ol className="list-disc pl-6 mt-2 space-y-1 text-sm text-slate-600">
                <li>Navigate to the Leads page</li>
                <li>Find the lead you want to enrich</li>
                <li>Click the "Enrich" button (sparkle icon)</li>
                <li>Wait 10-30 seconds for AI processing</li>
                <li>View the enriched data in the lead details</li>
              </ol>
            </li>
            <li>
              <strong>Bulk Enrichment (CSV Import)</strong>
              <ol className="list-disc pl-6 mt-2 space-y-1 text-sm text-slate-600">
                <li>Upload leads via CSV import</li>
                <li>System automatically queues enrichment</li>
                <li>Leads are processed in background</li>
                <li>Check enrichment status in queue manager</li>
              </ol>
            </li>
          </ol>
        </section>

        <section>
          <h3 className="text-xl font-semibold text-slate-900 mb-3">Enrichment Data Categories</h3>
          <div className="space-y-3">
            <div className="border rounded-lg p-4 bg-blue-50">
              <h4 className="font-semibold text-slate-900 mb-2">Company Overview</h4>
              <p className="text-slate-600 text-sm">Comprehensive company description, mission, and key business activities</p>
            </div>
            <div className="border rounded-lg p-4 bg-purple-50">
              <h4 className="font-semibold text-slate-900 mb-2">Business Model</h4>
              <p className="text-slate-600 text-sm">Revenue streams, target customers, and value proposition</p>
            </div>
            <div className="border rounded-lg p-4 bg-green-50">
              <h4 className="font-semibold text-slate-900 mb-2">Products & Services</h4>
              <p className="text-slate-600 text-sm">Detailed product/service catalog and offerings</p>
            </div>
            <div className="border rounded-lg p-4 bg-orange-50">
              <h4 className="font-semibold text-slate-900 mb-2">Team & Leadership</h4>
              <p className="text-slate-600 text-sm">Executive team, key decision makers, and org structure</p>
            </div>
            <div className="border rounded-lg p-4 bg-red-50">
              <h4 className="font-semibold text-slate-900 mb-2">Market Presence</h4>
              <p className="text-slate-600 text-sm">Geographic reach, market position, and competitive landscape</p>
            </div>
            <div className="border rounded-lg p-4 bg-indigo-50">
              <h4 className="font-semibold text-slate-900 mb-2">Technology Stack</h4>
              <p className="text-slate-600 text-sm">Technologies used, software platforms, and IT infrastructure</p>
            </div>
          </div>
        </section>

        <section>
          <h3 className="text-xl font-semibold text-slate-900 mb-3">AI Confidence Scores</h3>
          <p className="text-slate-700 mb-3">
            Each enriched lead receives an AI confidence score (0-100) indicating data quality:
          </p>
          <div className="space-y-2">
            <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded">
              <span className="px-3 py-1 bg-green-600 text-white font-bold rounded">85-100</span>
              <span className="text-sm text-slate-700">High confidence - Comprehensive, verified data</span>
            </div>
            <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded">
              <span className="px-3 py-1 bg-blue-600 text-white font-bold rounded">70-84</span>
              <span className="text-sm text-slate-700">Good confidence - Reliable with minor gaps</span>
            </div>
            <div className="flex items-center gap-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
              <span className="px-3 py-1 bg-yellow-600 text-white font-bold rounded">50-69</span>
              <span className="text-sm text-slate-700">Medium confidence - Partial data available</span>
            </div>
            <div className="flex items-center gap-3 p-3 bg-red-50 border border-red-200 rounded">
              <span className="px-3 py-1 bg-red-600 text-white font-bold rounded">0-49</span>
              <span className="text-sm text-slate-700">Low confidence - Limited data found</span>
            </div>
          </div>
        </section>

        <section>
          <h3 className="text-xl font-semibold text-slate-900 mb-3">Enrichment Queue Management</h3>
          <p className="text-slate-700 mb-3">
            Admin users can monitor and manage enrichment jobs:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-slate-700">
            <li><strong>Queue Status</strong>: View pending, processing, and completed enrichments</li>
            <li><strong>Rate Limiting</strong>: System processes 5 leads per minute to ensure quality</li>
            <li><strong>Retry Failed Jobs</strong>: Manually retry enrichments that failed</li>
            <li><strong>Priority Queue</strong>: Admin panel shows queue statistics</li>
          </ul>
        </section>

        <section>
          <h3 className="text-xl font-semibold text-slate-900 mb-3">Best Practices</h3>
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
            <ul className="space-y-2 text-slate-700 text-sm">
              <li className="flex items-start gap-2">
                <span className="text-indigo-600 font-bold">✓</span>
                <span>Enrich leads immediately after import for best context</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-indigo-600 font-bold">✓</span>
                <span>Provide at least company name and email for optimal results</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-indigo-600 font-bold">✓</span>
                <span>Review enriched data before using in outreach</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-indigo-600 font-bold">✓</span>
                <span>Re-enrich leads periodically (max 2 times) for fresh data</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-indigo-600 font-bold">✓</span>
                <span>Manually verify critical information before big deals</span>
              </li>
            </ul>
          </div>
        </section>

        <section>
          <h3 className="text-xl font-semibold text-slate-900 mb-3">Enrichment Costs</h3>
          <p className="text-slate-700 mb-3">
            Enrichment uses AI tokens and has associated costs:
          </p>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <ul className="space-y-1 text-slate-700 text-sm">
              <li>• Each enrichment uses ~4000-5000 AI tokens</li>
              <li>• Cost: approximately $0.005-0.010 per lead</li>
              <li>• Usage tracked in Admin Panel → API Usage Monitor</li>
              <li>• All plans include enrichment, costs from AI provider balance</li>
            </ul>
          </div>
        </section>
      </div>
    </DocLayout>
  );
};

export default Enrichment;